/** 后端接口客户端。错误 message 已是中文人话，可直接展示。 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    // 网络层失败：给的是「没有网络」而不是 TypeError: Failed to fetch
    throw new ApiError(0, 'NETWORK', '没有网络连接，请检查后再试')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let data: unknown = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = null
  }

  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: unknown; requestId?: string } })
      ?.error
    throw new ApiError(
      res.status,
      e?.code ?? 'UNKNOWN',
      e?.message ?? '出了点问题，请再试一次',
      e?.details,
      e?.requestId,
    )
  }
  return data as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body ?? {}),
}

// ── 类型 ──

export type Role = 'OWNER' | 'ASSISTANT'
export type Lang = 'ms' | 'en' | 'zh'
export type CaseType =
  | 'FULL_DENTURE'
  | 'PARTIAL_DENTURE'
  | 'REPAIR'
  | 'ADD_TOOTH'
  | 'RELINE'
  | 'ADJUSTMENT'
export type Arch = 'UPPER' | 'LOWER' | 'BOTH'
export type CaseStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'READY'
  | 'DELIVERED'
  | 'CLOSED'
  | 'ON_HOLD'
  | 'CANCELLED'
export type ApptStatus = 'BOOKED' | 'CONFIRMED' | 'ARRIVED' | 'DONE' | 'NO_SHOW' | 'CANCELLED'
export type PaymentMethod = 'CASH' | 'DUITNOW' | 'TRANSFER' | 'OTHER'
export type PaymentKind = 'DEPOSIT' | 'INSTALLMENT' | 'FINAL' | 'REFUND'

export interface Me {
  user: { id: string; name: string; role: Role }
  shop: {
    name: string
    setupDone: boolean
    /** 键是 0(周日)–6(周六)。值为 null = 当天休息。 */
    businessHours: Record<string, { open: string; close: string } | null>
  }
  channel: { healthy: boolean; configured: boolean }
}

export interface CustomerBrief {
  id: string
  fullName: string
  phoneE164: string
}

export interface SearchItem {
  id: string
  code: string
  fullName: string
  phoneE164: string
  phoneMasked: string
  language: Lang
  area: string | null
  lastVisitAt: string | null
  caseCount: number
  outstandingAmount: number | null
  hasOutstanding: boolean
}

export interface ToothMap {
  missing: number[]
  clasp: number[]
  replace: number[]
  notes?: string
}

export interface CaseStep {
  id: string
  seq: number
  code: string
  title: string
  status: 'PENDING' | 'DONE' | 'SKIPPED'
  plannedDate: string | null
  doneAt: string | null
  doneByName: string | null
  notes: string | null
  isCustom: boolean
}

export interface Payment {
  id: string
  kind: PaymentKind
  method: PaymentMethod
  amount: number
  paidAt: string
  note: string | null
  voidedAt: string | null
  voidReason: string | null
  voidedByName: string | null
}

export interface CaseDetail {
  id: string
  caseNo: string
  status: CaseStatus
  type: string
  /** 服务类型名。类型可由店主自定义，不能再靠前端的固定映射 */
  typeLabel: string
  arch: Arch | null
  toothMap: ToothMap
  customer: {
    id: string
    code: string
    fullName: string
    phoneE164: string
    language: Lang
    healthNotes: string | null
    optOut: boolean
  }
  quotedAmount: number
  discountAmount: number
  finalAmount: number
  paidAmount: number
  balanceAmount: number
  promisedDate: string | null
  daysUntilPromised: number | null
  readyAt: string | null
  deliveredAt: string | null
  notes: string | null
  steps: CaseStep[]
  payments: Payment[]
  photos: Array<{ id: string; kind: string; url: string; thumbUrl: string }>
  portalLink: {
    id: string
    expiresAt: string
    lastAccessedAt: string | null
    accessCount: number
  } | null
}

export interface CaseListItem {
  id: string
  caseNo: string
  type: string
  typeLabel: string
  arch: Arch | null
  status: CaseStatus
  customer: CustomerBrief
  /** 服务日期 —— 顾客实际到店那天，可能不是录入那天 */
  openedAt: string
  /** 补录的历史单：没有金额也没有流程，不进营业额 */
  isBackfill: boolean
  promisedDate: string | null
  finalAmount: number
  currentStep: string | null
  readyAt: string | null
  deliveredAt: string | null
  outstandingAmount: number | null
  hasOutstanding: boolean
}

export interface Appointment {
  id: string
  startsAt: string
  dateKey: string
  durationMin: number
  status: ApptStatus
  source: string
  purpose: string | null
  notes: string | null
  displayName: string
  phoneE164: string | null
  customer: CustomerBrief | null
  case: { id: string; caseNo: string; type: string } | null
  caseStep: { id: string; title: string; code: string } | null
}

export interface TodayData {
  date: string
  weekday: string
  isBusinessDay: boolean
  appointments: Appointment[]
  dueToday: Array<{
    id: string
    caseNo: string
    type: string
    typeLabel: string
    arch: Arch | null
    status: CaseStatus
    customer: CustomerBrief
    finalAmount: number
    paidAmount: number
    balanceAmount: number
  }>
  attention: {
    overdueCases: { count: number; maxOverdueDays: number }
    readyForPickup: { count: number; maxWaitingDays: number }
    outstanding: { count: number; totalAmount: number | null }
    manualContacts: { count: number }
  }
  channelHealthy: boolean
  channelConfigured: boolean
}

export interface ReminderItem {
  id: string
  type: string
  state: string
  customer: CustomerBrief
  case: { id: string; caseNo: string } | null
  toPhoneE164: string
  body: string
  scheduledAt: string
  sentAt: string | null
  attempts: number
  errorMessage: string | null
  /** 人工通知的凭据。顾客说「我没收到」时，这是唯一能回溯的东西。 */
  handledAt: string | null
  handledVia: 'WHATSAPP' | 'PHONE' | 'OTHER' | null
  handledByName: string | null
}

export interface CaseMeta {
  types: Array<{
    code: string
    label: string
    leadDays: number
    showsToothChart: boolean
    defaultAmount: number
  }>
  priceItems: Array<{
    id: string
    caseType: CaseType | null
    nameZh: string
    defaultAmount: number
  }>
}
